import type {
  LDClientBase,
  LDEvaluationDetail,
  LDInspectionFlagDetailsChangedHandler,
} from 'launchdarkly-js-sdk-common';
import { logException } from '../App/logging';

/*
  LaunchDarkly does not expose an API for listing the experiments a user is in.
  It does however tell us, per flag evaluation, whether that evaluation was the
  result of an experiment rollout (`reason.inExperiment`).

  An inspector is the only way to read reasons in bulk: `allFlags` returns values
  without reasons

  `evaluationReasons: true` must stay enabled on the client for `reason` to be
  populated — see createFeatureFlagProvider.
*/

let webAbTests: string[] = [];

/*
  Long enough to cover a normal flag fetch, short enough that a LaunchDarkly
  outage does not hold up identifying the user.
*/
const INITIALISATION_TIMEOUT_SECONDS = 5;

/*
  Values are formatted as `<flag-key>-<variation>`, e.g.
  `nova-web-welcome-conversation-true`
*/
const formatAbTest = (flagKey: string, value: unknown): string =>
  `${flagKey}-${String(value)}`;

/*
  JSON flags have no meaningful string form to build a cohort name from, so they
  are skipped rather than recorded as `<flag-key>-[object Object]`.
*/
const canFormatValue = (value: unknown): boolean =>
  ['boolean', 'string', 'number'].includes(typeof value);

/**
 * The experiments the current LaunchDarkly context is enrolled in, as
 * `webAbTests` values.
 *
 * Waits for the client to have flags before reading them, so callers are not
 * exposed to the gap between page load and the first flag fetch.
 *
 * Returns `undefined` when the experiments could not be determined, rather than
 * throwing or reporting an empty result: callers should leave whatever they hold
 * untouched so an intermittent LaunchDarkly failure cannot wipe a user's
 * recorded experiments. An empty array means the user is genuinely in none.
 */
export const getExperimentAbTests = async (
  client: LDClientBase | undefined,
): Promise<string[] | undefined> => {
  if (!client) {
    return undefined;
  }

  try {
    await client.waitForInitialization(INITIALISATION_TIMEOUT_SECONDS);
  } catch (e) {
    logException(e, { tags: { feature: 'experiments' } });

    return undefined;
  }

  return webAbTests;
};

/**
 * Collects the experiment values read by `getExperimentAbTests`. LaunchDarkly
 * calls this before initialisation resolves, and again whenever the flag store
 * is replaced — which includes every `identify`, so the values always describe
 * the context the client currently holds.
 */
export const experimentsInspector: LDInspectionFlagDetailsChangedHandler = {
  type: 'flag-details-changed',
  name: 'experiments',
  method: (details: Record<string, LDEvaluationDetail>) => {
    webAbTests = Object.entries(details)
      .filter(
        ([, detail]) =>
          detail.reason?.inExperiment && canFormatValue(detail.value),
      )
      .map(([flagKey, detail]) => formatAbTest(flagKey, detail.value))
      .sort();
  },
};
